/**
 * Service for fetching support dashboard data from the API
 */

const fetchWithAuth = async (endpoint, options = {}) => {
  const API_BASE_URL = (process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_VERCEL_API_URL
    : process.env.REACT_APP_LOCAL_API_URL) + 'api';

  const userToken_TOVIAPPS = localStorage.getItem('userToken_TOVIAPPS');

  if (!userToken_TOVIAPPS) {
    throw new Error('Token de autenticação não encontrado. Por favor, faça login novamente.');
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${userToken_TOVIAPPS}`,
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, { ...options, headers });

  if (response.status === 401) {
    localStorage.removeItem('userToken_TOVIAPPS');
    throw new Error('Sua sessão expirou ou o token é inválido. Por favor, faça login novamente.');
  }

  if (response.status === 403) {
    throw new Error('Você não tem permissão para acessar esta página.');
  }

  if (!response.ok) {
    throw new Error(`Falha na requisição da API com status ${response.status}`);
  }

  return response;
};

/**
 * Fetch support dashboard data from the API
 * @param {string} timeFrame - Time frame for data (day, week, month, quarter, year)
 * @returns {Promise} - Promise with dashboard data
 */
export const fetchDashboardData = async (timeFrame) => {
  const response = await fetchWithAuth(`/dashboard/suporte/stats?timeFrame=${timeFrame}`, {
    method: 'GET',
  });
  const data = await response.json();

  // Map backend response to the shape the frontend components expect
  const stats = {
    ticketsAbertos: data.stats?.ticketsAbertos || { value: 0, change: 0 },
    ticketsResolvidos: data.stats?.ticketsResolvidos || { value: 0, change: 0 },
    tempoMedioResposta: { value: 0, change: 0 }, // Not tracked yet
    tempoMedioResolucao: { value: 0, change: 0 }, // Not tracked yet
    satisfacaoCliente: { value: 0, change: 0 }, // Not tracked yet
    ticketsReabertos: { value: 0, change: 0 }, // Not tracked yet
    contatosPorDia: data.stats?.totalNoPeriodo || { value: 0, change: 0 },
    atendimentosPrioritarios: data.stats?.ticketsAguardandoConfirmacao || { value: 0, change: 0 },
  };

  // Map recent tickets
  const ticketsRecentes = (data.ticketsRecentes || []).map(t => ({
    id: t.id,
    cliente: '-',
    assunto: t.categoria,
    prioridade: t.prioridade,
    status: t.status,
    categoria: t.categoria,
    tempo: formatTimeSince(t.createdAt),
    atendente: '-',
  }));

  // Map categories
  const categoriasTickets = (data.categoriasTickets || []).map(c => ({
    nome: c.nome,
    quantidade: c.quantidade,
    percentual: c.percentual,
    tempoMedio: 0,
  }));

  return {
    stats,
    ticketsRecentes,
    categoriasTickets,
    equipeStatus: [], // Not tracked yet — admin team status not in backend
    faqPopulares: [], // Not tracked yet
    canaisAtendimento: [], // Not tracked yet — single channel (app)
    trend: data.trend || [],
  };
};

function formatTimeSince(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}
